/**
 * Tabela genérica.
 *
 * Não é só conveniência: toda visualização deste projeto tem uma tabela
 * equivalente ao lado. Isso é requisito de acessibilidade — três das oito cores
 * de série ficam abaixo de 3:1 contra a superfície clara, e a compensação
 * exigida é que o valor esteja legível fora do gráfico. Também é o que torna o
 * número conferível contra a planilha, que é como a área de negócio trabalha.
 */

import type { ReactNode } from 'react'

import estilos from './Tabela.module.css'

export type Coluna<T> = {
  chave: string
  cabecalho: string
  /** `numero` alinha à direita e liga `tabular-nums` — colunas devem alinhar. */
  tipo?: 'texto' | 'numero'
  largura?: string
  celula: (linha: T) => ReactNode
}

export function Tabela<T>({
  colunas,
  linhas,
  chaveDe,
  rotulo,
  aoClicar,
  vazio = 'Nenhuma linha.',
}: {
  colunas: Coluna<T>[]
  linhas: T[]
  chaveDe: (linha: T, indice: number) => string | number
  rotulo: string
  aoClicar?: (linha: T) => void
  vazio?: ReactNode
}) {
  return (
    // A rolagem horizontal vive no contêiner da tabela; a página nunca rola
    // para o lado.
    <div className={estilos.moldura}>
      <table className={estilos.tabela}>
        <caption className={estilos.legenda}>{rotulo}</caption>
        <thead>
          <tr>
            {colunas.map((coluna) => (
              <th
                key={coluna.chave}
                scope="col"
                style={{ width: coluna.largura }}
                className={coluna.tipo === 'numero' ? estilos.numero : undefined}
              >
                {coluna.cabecalho}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {linhas.length === 0 && (
            <tr>
              <td colSpan={colunas.length} className={estilos.semLinhas}>
                {vazio}
              </td>
            </tr>
          )}
          {linhas.map((linha, indice) => (
            <tr
              key={chaveDe(linha, indice)}
              className={aoClicar ? estilos.clicavel : undefined}
              onClick={aoClicar ? () => aoClicar(linha) : undefined}
            >
              {colunas.map((coluna) => (
                <td
                  key={coluna.chave}
                  className={coluna.tipo === 'numero' ? estilos.numero : undefined}
                >
                  {coluna.celula(linha)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
